// Showroom 360° de l'accueil.
//
// - La section est épinglée (GSAP ScrollTrigger) pendant 0,6 hauteur d'écran
//   (entre 360 et 640 px) : le défilement fait tourner la moto de la vue 1
//   (profil) à la vue 15 (face), en quatre ou cinq crans de molette.
// - Rotation portée par un ressort amorti : la moto garde son élan d'un cran de
//   molette à l'autre au lieu de repartir de zéro. Fondu enchaîné entre deux
//   vues voisines pendant le mouvement ; à l'arrêt, une seule vue, nette. Le nom
//   géant glisse légèrement derrière la moto (parallaxe).
// - Changement de modèle : onglets par famille (clavier : flèches, Début, Fin),
//   boutons des modèles, flèches ‹ ›, ou glissement horizontal sur la moto.
//   Le nouveau modèle arrive toujours de profil, au début de la course.
// - Chargement : la vue affichée d'abord (priorité haute), puis une vue sur
//   quatre, une sur deux, et les autres : sur une connexion lente, toute la
//   rotation est vite couverte, puis s'affine. Le modèle suivant n'est chargé
//   qu'une fois le modèle affiché complet.
// - /?modele=fat-boy ouvre un modèle précis (l'adresse suit le modèle affiché).
// - « Réduire les animations » ou GSAP absent : pas d'épinglage, vue de 3/4
//   et un curseur pour tourner la moto.

import { CutoutLoader } from "./cutout.js";
import { FRAME_COUNT as N, frameUrl, getCatalog, maskUrl } from "./frames.js";
import { nextBlend, paint, spring } from "./stage.js";

// Glissement du nom géant sur toute la rotation, en % de sa largeur, de part et
// d'autre du centre : un nom long, ajusté à 98 % de la largeur, n'est pas coupé.
const DRIFT = 3;

const root = document.querySelector("[data-showroom]");
if (root) showroom(root);

function showroom(root) {
  const { models } = getCatalog();
  const width = matchMedia("(max-width: 700px)").matches ? 800 : 1200;
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const { gsap, ScrollTrigger } = window;
  const pinned = !reduce && !!gsap && !!ScrollTrigger;

  const $ = (s) => root.querySelector(s);
  const canvas = $(".showroom-canvas");
  const ctx = canvas.getContext("2d");
  const nameEl = $(".showroom-name");
  const loadingEl = $(".showroom-loading");
  const panel = $('[role="tabpanel"]');
  const angle = $(".showroom-angle");
  const hint = $(".showroom-hint");
  const prevBtn = $(".showroom-arrow.prev");
  const nextBtn = $(".showroom-arrow.next");
  const swipeHint = $(".showroom-scroll-hint");
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  const chips = [...root.querySelectorAll(".model-chip")];
  const infos = [...root.querySelectorAll(".showroom-info")];

  // Nom centré par GSAP en pourcentages : reste centré quand sa taille change.
  if (gsap && !reduce) gsap.set(nameEl, { xPercent: -50, yPercent: -50, x: 0, y: 0 });
  const drift = pinned ? gsap.quickSetter(nameEl, "xPercent") : null;

  const loader = new CutoutLoader({ capacity: 32 });
  const key = (m, i) => `${m.id}:${i}:${width}`; // i : vue 0 à 14
  const failures = new Map();

  let current = Math.max(0, models.findIndex((m) => m.id === new URLSearchParams(location.search).get("modele")));
  let shown = current; // modèle dessiné (diffère de `current` pendant un changement)
  const HOME = pinned ? 0 : 7; // vue de départ : profil (épinglé) ou trois quarts (curseur)
  let trigger = null; // ScrollTrigger de la rotation
  let frame = HOME;
  let target = frame;
  let vel = 0; // vitesse de rotation, en vues par seconde
  let blend = 0; // force du fondu entre vues voisines (0 à l'arrêt)
  let fade = 1;
  let raf = 0;
  let last = 0;
  let lastNear = -1;
  let dpr = 1;
  let ready = false; // passe à true une fois le showroom affiché
  const prefetched = new Set();

  // ------------------------------------------------------------ chargement

  // Vue `from`, puis une vue sur quatre (et la dernière), une sur deux, les
  // autres ; chaque palier de la plus proche à la plus lointaine.
  const level = (i) => (i % 4 === 0 || i === N - 1 ? 0 : i % 2 === 0 ? 1 : 2);
  const loadOrder = (from) =>
    [...Array(N).keys()].sort((a, b) => (a !== from) - (b !== from) || level(a) - level(b) || Math.abs(a - from) - Math.abs(b - from) || a - b);

  function request() {
    const m = models[current];
    const near = Math.round(target); // pendant un changement, `frame` est encore celle de l'ancien modèle
    const next = models[(current + 1) % models.length];
    loader.keepOnly((job) => job.tag === m.id || job.tag === "essai" || (job.tag === next.id && prefetched.has(next.id)));
    loadOrder(near).forEach((i, rank) => {
      loader
        .load(key(m, i), frameUrl(m, i + 1, width), maskUrl(m, i + 1, width), {
          prio: rank,
          tag: m.id,
          fetchPriority: rank === 0 ? "high" : "auto",
        })
        .then(() => loaded(m), (err) => failed(m, err));
    });
  }

  function loaded(m) {
    wake();
    if (m !== models[current] || !complete(m)) return;
    const next = models[(current + 1) % models.length];
    if (prefetched.has(next.id)) return;
    prefetched.add(next.id);
    loadOrder(0).forEach((i, rank) =>
      loader
        .load(key(next, i), frameUrl(next, i + 1, width), maskUrl(next, i + 1, width), { prio: 100 + rank, tag: next.id, fetchPriority: "low" })
        .catch(() => {}),
    );
  }

  function failed(m, err) {
    if (err?.cancelled) return;
    failures.set(m.id, (failures.get(m.id) ?? 0) + 1);
    console.warn(err);
    wake();
  }

  const complete = (m) => [...Array(N).keys()].every((i) => loader.has(key(m, i)));

  function reprioritize() {
    const m = models[current];
    const rank = new Map(loadOrder(Math.round(frame)).map((i, r) => [i, r]));
    loader.reprioritize((job) => {
      const [id, i] = job.key.split(":");
      return id === m.id ? rank.get(Number(i)) : 100 + Number(i);
    });
  }

  // Vue prête la plus proche de i (pendant le chargement).
  function nearest(m, i) {
    for (let d = 0; d < N; d++) {
      const a = loader.get(key(m, i - d)) ?? loader.get(key(m, i + d));
      if (a) return a;
    }
    return null;
  }

  // ------------------------------------------------------------ dessin

  function resize() {
    const r = canvas.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(r.width * dpr));
    canvas.height = Math.max(1, Math.round(r.height * dpr));
    fitName();
    draw();
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const m = models[shown];
    const f = Math.min(N - 1, Math.max(0, frame));
    drift?.(-50 + DRIFT * (f / (N - 1) - 0.5));
    const i0 = Math.floor(f);
    const t = f - i0;
    const i1 = Math.min(i0 + 1, N - 1);
    const dom = nearest(m, t < 0.5 ? i0 : i1);
    const waiting = shown !== current && fade === 0;
    const broken = (failures.get(models[current].id) ?? 0) >= N;
    loadingEl.hidden = !broken && !!dom && !waiting;
    loadingEl.textContent = broken ? "Photos indisponibles pour le moment" : "Chargement de la moto";
    if (!dom) return;
    // Fondu enchaîné entre les deux vues voisines (50/50 au milieu), seulement
    // pendant le mouvement : la vue dominante reste opaque.
    const mix = (0.5 - Math.abs(t - 0.5)) * blend;
    const other = mix > 0.01 ? loader.get(key(m, t < 0.5 ? i1 : i0)) : null;
    paint(ctx, { dom, other, mix, fade, frameBox: m.box, dpr });
  }

  function tick(now) {
    raf = 0;
    const dt = Math.min(50, last ? now - last : 16) / 1000;
    last = now;
    if (shown === current) {
      // Ressort de rotation (stage.js) : la vitesse est conservée d'un cran de molette
      // à l'autre, et la moto s'arrête sans rebondir.
      [frame, vel] = spring(frame, vel, target, dt);
      if (fade < 1) fade = Math.min(1, fade + dt / 0.22);
    } else {
      // Changement de modèle : l'ancien s'efface sans tourner, le nouveau
      // apparaît directement à sa vue de départ.
      fade = Math.max(0, fade - dt / 0.14);
      if (fade === 0 && nearest(models[current], Math.round(target))) {
        shown = current;
        frame = target;
        vel = 0;
      }
    }
    blend = nextBlend(blend, vel, dt);
    const near = Math.round(shown === current ? frame : target);
    if (near !== lastNear) {
      lastNear = near;
      reprioritize();
    }
    draw();
    const waiting = shown !== current && fade === 0;
    if ((!waiting && (frame !== target || fade < 1 || shown !== current)) || blend > 0) wake();
    else last = 0;
  }

  function wake() {
    if (!raf) raf = requestAnimationFrame(tick);
  }

  // ------------------------------------------------------------ interface

  function fitName() {
    nameEl.style.fontSize = "";
    const max = root.clientWidth * 0.98;
    const w = nameEl.scrollWidth;
    if (w > max) nameEl.style.fontSize = `${(parseFloat(getComputedStyle(nameEl).fontSize) * max) / w}px`;
  }

  function setName(m) {
    nameEl.textContent = m.short ?? m.name;
    fitName();
    if (gsap && !reduce) gsap.fromTo(nameEl, { yPercent: -42, opacity: 0 }, { yPercent: -50, opacity: 0.85, duration: 0.6, ease: "power3.out" });
  }

  function updateUI() {
    const m = models[current];
    for (const t of tabs) {
      const on = t.dataset.family === m.family;
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
    }
    panel.setAttribute("aria-labelledby", `tab-${m.family}`);
    for (const c of chips) {
      c.hidden = c.dataset.family !== m.family;
      c.setAttribute("aria-current", String(c.dataset.model === m.id));
    }
    for (const el of infos) el.hidden = el.dataset.model !== m.id;
    const n = models.length;
    prevBtn.setAttribute("aria-label", `Modèle précédent : ${models[(current - 1 + n) % n].name}`);
    nextBtn.setAttribute("aria-label", `Modèle suivant : ${models[(current + 1) % n].name}`);
    canvas.setAttribute("aria-label", `${m.name}, vue à 360°`);
    // Sur mobile, les rangées défilent : on amène l'onglet et le modèle actifs à l'écran.
    revealActive(!reduce && ready);
    setName(m);
  }

  function revealActive(smooth) {
    const m = models[current];
    for (const el of [tabs.find((t) => t.dataset.family === m.family), chips.find((c) => c.dataset.model === m.id)]) {
      const row = el?.parentElement;
      if (!row || row.scrollWidth <= row.clientWidth) continue;
      const left = row.scrollLeft + el.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - el.offsetWidth) / 2;
      row.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
    }
  }

  function select(i) {
    const n = models.length;
    i = ((i % n) + n) % n;
    if (i === current) return;
    current = i;
    goHome();
    updateUI();
    request();
    wake();
    const url = new URL(location.href);
    url.searchParams.set("modele", models[i].id);
    history.replaceState(history.state, "", url);
  }

  // Le nouveau modèle arrive « droit », à la vue de départ, quel que soit l'angle
  // où le visiteur avait tourné le précédent. La rotation suit le défilement :
  // on ramène donc aussi la page au début de la course (invisible, la section
  // est épinglée), sinon le prochain cran de molette ferait sauter l'angle.
  function goHome() {
    target = HOME;
    vel = 0;
    if (trigger && window.scrollY > trigger.start + 1) window.scrollTo({ top: trigger.start, behavior: "instant" });
    if (!pinned) angle.value = String(HOME + 1);
  }

  const selectFamily = (f) => models[current].family !== f && select(models.findIndex((m) => m.family === f));

  tabs.forEach((t, k) => {
    t.addEventListener("click", () => selectFamily(t.dataset.family));
    t.addEventListener("keydown", (e) => {
      const j = { ArrowRight: k + 1, ArrowLeft: k - 1, Home: 0, End: tabs.length - 1 }[e.key];
      if (j === undefined) return;
      e.preventDefault();
      const next = tabs[(j + tabs.length) % tabs.length];
      next.focus();
      selectFamily(next.dataset.family);
    });
  });
  for (const c of chips) c.addEventListener("click", () => select(models.findIndex((m) => m.id === c.dataset.model)));
  prevBtn.addEventListener("click", () => select(current - 1));
  nextBtn.addEventListener("click", () => select(current + 1));

  // Glisser horizontalement sur la moto : modèle suivant ou précédent.
  let start = null;
  canvas.addEventListener("pointerdown", (e) => (start = { x: e.clientX, y: e.clientY }));
  canvas.addEventListener("pointercancel", () => (start = null));
  canvas.addEventListener("pointerup", (e) => {
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    start = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) select(current + (dx < 0 ? 1 : -1));
  });

  // ------------------------------------------------------------ rotation

  if (pinned) {
    gsap.registerPlugin(ScrollTrigger);
    const headerH = () => document.querySelector(".site-header")?.offsetHeight ?? 0;
    trigger = ScrollTrigger.create({
      trigger: root,
      start: () => `top ${headerH()}px`,
      // Course courte : la moto fait son quart de tour en quatre ou cinq crans de molette.
      end: () => `+=${Math.round(Math.min(640, Math.max(360, window.innerHeight * 0.6)))}`,
      pin: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        target = self.progress * (N - 1);
        if (self.progress > 0.04) swipeHint?.classList.add("is-gone");
        wake();
      },
    });
    // L'épinglage allonge la page : on recale une ancre (/#essai) visée à l'arrivée.
    const hash = decodeURIComponent(location.hash.slice(1));
    const anchor = hash && hash !== "gamme" && document.getElementById(hash);
    if (anchor) {
      anchor.scrollIntoView();
      addEventListener("load", () => anchor.scrollIntoView(), { once: true });
    }
  } else {
    hint.hidden = true;
    if (swipeHint) swipeHint.hidden = true;
    angle.hidden = false;
    angle.value = String(HOME + 1);
    angle.addEventListener("input", () => {
      target = Number(angle.value) - 1;
      if (reduce) frame = target;
      wake();
    });
  }

  new ResizeObserver(resize).observe(canvas);
  document.fonts?.ready.then(() => {
    fitName();
    revealActive(false);
  });
  // L'épinglage peut réinsérer la section dans la page, ce qui remet les rangées au début.
  ScrollTrigger?.addEventListener("refresh", () => revealActive(false));
  updateUI();
  request();
  resize();
  ready = true;
  essaiPreview({ models, loader, key, width, current: () => models[current] });
}

// Section « Tout commence par un essai » : la moto choisie dans le formulaire
// s'affiche, détourée, avec son nom en grand. Vue 8 (trois quarts), prise dans
// le cache du showroom quand elle y est déjà.
function essaiPreview({ models, loader, key, width, current }) {
  const box = document.querySelector("[data-essai-bike]");
  const select = document.querySelector('#form-essai select[name="modele"]');
  if (!box || !select) return;
  const canvas = box.querySelector("canvas");
  const nameEl = box.querySelector(".essai-bike-name");
  let shown = null;
  let visible = false;

  async function show() {
    if (!visible) return;
    // Rien de choisi : on propose la moto que le visiteur regardait dans le showroom.
    if (!select.value) select.value = current().name;
    const m = models.find((x) => x.name === select.value) ?? current();
    if (m === shown) return;
    shown = m;
    nameEl.textContent = m.short ?? m.name;
    box.classList.remove("is-ready");
    try {
      const { bitmap, box: b } = await loader.load(key(m, 7), frameUrl(m, 8, width), maskUrl(m, 8, width), { prio: 50, tag: "essai" });
      if (shown !== m) return;
      const pad = Math.round(bitmap.width * 0.01);
      const x = Math.max(0, b.x0 - pad);
      const y = Math.max(0, b.y0 - pad);
      const w = Math.min(bitmap.width, b.x1 + pad) - x;
      const h = Math.min(bitmap.height, b.y1 + pad) - y;
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(bitmap, x, y, w, h, 0, 0, w, h);
      box.classList.add("is-ready");
    } catch (err) {
      if (!err?.cancelled) console.warn(err);
      shown = null;
    }
  }

  select.addEventListener("change", show);
  // Chargement seulement à l'approche de la section, pour ne pas ralentir le showroom.
  new IntersectionObserver(
    (entries, io) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      visible = true;
      show();
    },
    { rootMargin: "600px 0px" },
  ).observe(box);
}
